import { useAuth0 } from '@auth0/auth0-react';
import { apiConfig } from '../config/auth0-config';

export interface ApiResponse {
  message: string;
  timestamp: string;
  user: {
    id: string;
    scope: string[];
    audience: string;
  };
  request: {
    method: string;
    path: string;
    headers: Record<string, string>;
    body: any;
  };
  demo: {
    description: string;
    endpoints: Record<string, string>;
  };
}

export interface ApiError {
  error: string;
  message: string;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiConfig.baseUrl;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    accessToken?: string
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    console.log('Making API request:', {
      url,
      method: config.method || 'GET',
      headers,
    });

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API request failed:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });

      let errorData: ApiError;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = {
          error: 'Request Failed',
          message: `${response.status} ${response.statusText}`,
        };
      }

      throw new Error(
        errorData.message || `Request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    console.log('API response:', data);
    return data;
  }

  async getProtectedData(accessToken: string): Promise<ApiResponse> {
    return this.makeRequest<ApiResponse>(
      apiConfig.endpoints.protected,
      { method: 'GET' },
      accessToken
    );
  }

  async postProtectedData(
    accessToken: string,
    data: any
  ): Promise<ApiResponse> {
    return this.makeRequest<ApiResponse>(
      apiConfig.endpoints.protected,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      accessToken
    );
  }

  async getUserProfile(accessToken: string): Promise<any> {
    const url = 'https://develop.api.swanvision.ai/user/profile';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };

    console.log('Making App User Profile API request:', { url, headers });

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('User Profile API request failed:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });

      throw new Error(
        `User Profile API request failed with status ${response.status}: ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('User Profile API response:', data);
    return data;
  }
}

export const apiService = new ApiService();

export const useApiService = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const callProtectedApi = async (
    method: 'GET' | 'POST' = 'GET',
    data?: any
  ): Promise<ApiResponse> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'https://develop.api.swanvision.ai',
          scope: 'openid profile email offline_access',
        },
      });
      console.log('Access Token:', accessToken);

      if (method === 'GET') {
        return apiService.getProtectedData(accessToken);
      } else {
        return apiService.postProtectedData(accessToken, data);
      }
    } catch (error) {
      console.error('Failed to get access token or call API:', error);
      throw error;
    }
  };

  const callUserProfile = async (): Promise<any> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }

    try {
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'https://develop.api.swanvision.ai',
          scope: 'openid profile email offline_access',
        },
      });

      return apiService.getUserProfile(accessToken);
    } catch (error) {
      console.error(
        'Failed to get access token or call user profile API:',
        error
      );
      throw error;
    }
  };

  return {
    callProtectedApi,
    callUserProfile,
  };
};
